import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, CheckCircle, Prohibit, PencilSimple, Trash, Calendar, QrCode, Copy, ArrowClockwise, Warning, Eye,
} from "@phosphor-icons/react";

const empty = { name: "", owner_email: "", owner_name: "", owner_password: "", plan_id: "" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso.slice(0, 10); }
};

const isoToInput = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const diff = new Date(iso).getTime() - Date.now();
    return Math.ceil(diff / 86400000);
  } catch { return null; }
};

export default function MasterCompanies() {
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [dating, setDating] = useState(null);
  const [pix, setPix] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/companies"), api.get("/plans")]);
    setCompanies(c.data);
    setPlans(p.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/companies", form);
      toast.success("Empresa criada");
      setOpenNew(false);
      setForm(empty);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "blocked" : "active";
    try {
      await api.patch(`/companies/${c.id}/status`, { status: next });
      toast.success(next === "active" ? "Empresa reativada (+30 dias)" : "Empresa bloqueada");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteCompany = async () => {
    try {
      await api.delete(`/companies/${deleting.id}`);
      toast.success("Empresa excluída");
      setDeleting(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const impersonate = async (c) => {
    try {
      const { data } = await api.post(`/master/impersonate/company/${c.id}`);
      const url = `/impersonate?token=${encodeURIComponent(data.token)}&redirect=${encodeURIComponent(data.redirect)}`;
      window.open(url, "_blank", "noopener");
      toast.success(`Abrindo painel de ${data.company_name} em nova aba`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Master</p>
          <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Empresas</h1>
          <p className="text-sm text-[#5C5C59] mt-2">Gerencie clientes, cobranças mensais e acesso.</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button data-testid="new-company-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none shadow-brutal">
              <Plus size={18} weight="bold" className="mr-2" /> Nova empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-lg">
            <DialogHeader><DialogTitle>Nova empresa cliente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Nome da empresa</Label>
                <Input data-testid="company-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Nome do responsável</Label>
                  <Input data-testid="company-owner-name-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">E-mail</Label>
                  <Input data-testid="company-owner-email-input" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="rounded-none mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Senha inicial</Label>
                  <Input data-testid="company-owner-password-input" type="text" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Plano</Label>
                  <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                    <SelectTrigger data-testid="company-plan-select" className="rounded-none mt-1"><SelectValue placeholder="Selecionar plano" /></SelectTrigger>
                    <SelectContent className="bg-white rounded-none">
                      {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-[#5C5C59]">A primeira cobrança vence em 30 dias. O sistema bloqueia automaticamente se não pagar.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpenNew(false)} className="rounded-none">Cancelar</Button>
              <Button data-testid="company-save-btn" onClick={create} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Criar empresa</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E5E5E0] shadow-brutal" data-testid="companies-table">
        <div className="grid grid-cols-[1.5fr_1fr_1.2fr_1fr_1fr_260px] gap-3 px-4 py-3 border-b border-[#E5E5E0] text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5C5C59]">
          <div>Empresa</div>
          <div>Plano</div>
          <div>Responsável</div>
          <div>Próx. cobrança</div>
          <div>Status</div>
          <div className="text-right">Ações</div>
        </div>
        {companies.length === 0 && (
          <div className="p-8 text-center text-sm text-[#5C5C59]">Nenhuma empresa cadastrada.</div>
        )}
        {companies.map((c) => {
          const days = daysUntil(c.next_billing_at);
          const overdue = c.status === "blocked" && c.blocked_reason === "unpaid";
          const dueSoon = c.status === "active" && days !== null && days <= 3;
          return (
            <div key={c.id} className="grid grid-cols-[1.5fr_1fr_1.2fr_1fr_1fr_260px] gap-3 px-4 py-3 border-b border-[#E5E5E0] items-center text-sm hover:bg-[#F7F7F5]">
              <div className="font-semibold truncate">{c.name}</div>
              <div>{c.plan_name || "—"}</div>
              <div className="font-mono text-xs text-[#5C5C59] truncate">{c.owner_email}</div>
              <div className={dueSoon ? "text-[#D97706] font-semibold" : ""}>
                <div className="text-xs">{fmtDate(c.next_billing_at)}</div>
                {days !== null && c.status === "active" && (
                  <div className="text-[10px] text-[#5C5C59]">
                    {days > 0 ? `em ${days}d` : `venceu ${Math.abs(days)}d atrás`}
                  </div>
                )}
              </div>
              <div>
                {c.status === "active" ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F4C3A]"><CheckCircle size={14} weight="fill" /> Ativa</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#DC2626]">
                    <Prohibit size={14} weight="fill" /> {overdue ? "Inadimplente" : "Bloqueada"}
                  </span>
                )}
              </div>
              <div className="flex justify-end gap-1 flex-wrap">
                <IconAction icon={Eye} label="Entrar como" onClick={() => impersonate(c)} testid={`impersonate-${c.id}`} variant="primary" />
                <IconAction icon={PencilSimple} label="Editar" onClick={() => setEditing({ ...c, owner_name: "" })} testid={`edit-${c.id}`} />
                <IconAction icon={Calendar} label="Alterar data" onClick={() => setDating(c)} testid={`date-${c.id}`} />
                <IconAction icon={QrCode} label="2ª via Pix" onClick={() => setPix({ company: c, checkout: null, cpf: "" })} testid={`pix-${c.id}`} />
                <IconAction
                  icon={c.status === "active" ? Prohibit : ArrowClockwise}
                  label={c.status === "active" ? "Bloquear" : "Reativar"}
                  onClick={() => toggleStatus(c)}
                  testid={`toggle-${c.id}`}
                  variant={c.status === "active" ? "danger" : "success"}
                />
                <IconAction icon={Trash} label="Excluir" onClick={() => setDeleting(c)} testid={`delete-${c.id}`} variant="danger" />
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <EditDialog
          company={editing}
          plans={plans}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {dating && (
        <BillingDateDialog
          company={dating}
          onClose={() => setDating(null)}
          onSaved={() => { setDating(null); load(); }}
        />
      )}
      {pix && (
        <RegenPixDialog
          state={pix}
          setState={setPix}
          onClose={() => { setPix(null); load(); }}
        />
      )}
      {deleting && (
        <Dialog open onOpenChange={(o) => !o && setDeleting(null)}>
          <DialogContent className="bg-white rounded-none border-2 border-[#DC2626] max-w-md" data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#DC2626]">
                <Warning size={20} weight="fill" /> Excluir empresa
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm">
              Isso vai remover <b>{deleting.name}</b>, todos os tickets, mensagens, usuários e conexões WhatsApp associadas. <b>Não dá pra desfazer.</b>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleting(null)} className="rounded-none">Cancelar</Button>
              <Button data-testid="confirm-delete-btn" onClick={deleteCompany} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-none">
                Sim, excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function IconAction({ icon: Icon, label, onClick, testid, variant }) {
  const cls = variant === "danger"
    ? "border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
    : variant === "success"
      ? "border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
      : variant === "primary"
        ? "border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white"
        : "border-[#E5E5E0] text-[#5C5C59] hover:border-[#0F4C3A] hover:text-[#0F4C3A]";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={label}
      className={`inline-flex items-center gap-1 text-[10px] px-2 py-1.5 border transition-colors ${cls}`}
    >
      <Icon size={12} weight="bold" />
      <span className="hidden xl:inline">{label}</span>
    </button>
  );
}

function EditDialog({ company, plans, onClose, onSaved }) {
  const [form, setForm] = useState({ name: company.name, plan_id: company.plan_id, owner_name: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (form.name && form.name !== company.name) payload.name = form.name;
      if (form.plan_id !== company.plan_id) payload.plan_id = form.plan_id;
      if (form.owner_name.trim()) payload.owner_name = form.owner_name.trim();
      await api.put(`/companies/${company.id}`, payload);
      toast.success("Empresa atualizada");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-none max-w-lg" data-testid="edit-dialog">
        <DialogHeader><DialogTitle>Editar empresa</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Nome da empresa</Label>
            <Input data-testid="edit-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Plano</Label>
            <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
              <SelectTrigger data-testid="edit-plan-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white rounded-none">
                {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — R$ {p.price.toFixed(2)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Novo nome do responsável (opcional)</Label>
            <Input data-testid="edit-owner-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-none mt-1" placeholder="Deixe em branco pra manter" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancelar</Button>
          <Button data-testid="edit-save-btn" onClick={save} disabled={saving} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BillingDateDialog({ company, onClose, onSaved }) {
  const [date, setDate] = useState(isoToInput(company.next_billing_at) || new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const iso = new Date(date + "T12:00:00Z").toISOString();
      await api.patch(`/companies/${company.id}/billing-date`, { next_billing_at: iso });
      toast.success("Data de cobrança atualizada");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-none max-w-md" data-testid="billing-date-dialog">
        <DialogHeader><DialogTitle>Alterar próxima cobrança</DialogTitle></DialogHeader>
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold">Nova data</Label>
          <Input data-testid="billing-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none mt-1" />
          <p className="text-xs text-[#5C5C59] mt-2">
            Empresa: <b>{company.name}</b>. Alterar a data adia (ou antecipa) o próximo bloqueio automático.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancelar</Button>
          <Button data-testid="billing-date-save-btn" onClick={save} disabled={saving} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegenPixDialog({ state, setState, onClose }) {
  const { company, checkout, cpf } = state;
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Poll for approval
  useEffect(() => {
    if (!checkout?.purchase_id || checkout.status === "approved") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/companies/${company.id}/pix/${checkout.purchase_id}`);
        setState((s) => s ? { ...s, checkout: { ...s.checkout, ...data } } : s);
        if (data.status === "approved") {
          toast.success("Pagamento aprovado! Empresa reativada.");
          clearInterval(t);
        }
      } catch (e) { console.debug("[master-companies] pix poll (retry):", e?.message || e); }
    }, 5000);
    return () => clearInterval(t);
  }, [checkout?.purchase_id, checkout?.status, company.id, setState]);

  const generate = async () => {
    const cpfClean = cpf.replace(/\D/g, "");
    if (cpfClean.length < 11) return toast.error("CPF inválido");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/pix`, { payer_cpf: cpfClean });
      setState((s) => ({ ...s, checkout: data }));
      toast.success("Pix gerado");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(checkout?.qr_code || "");
    setCopied(true);
    toast.success("Código Pix copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-none max-w-xl" data-testid="pix-dialog">
        <DialogHeader>
          <DialogTitle>2ª via Pix — {company.name}</DialogTitle>
        </DialogHeader>
        {!checkout ? (
          <div className="space-y-3">
            <p className="text-sm text-[#5C5C59]">
              Gera um novo QR Code Pix para renovação mensal. Após pagamento, a empresa é reativada e a próxima cobrança é agendada em 30 dias.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">CPF do pagador</Label>
              <Input data-testid="pix-cpf-input" value={cpf} onChange={(e) => setState((s) => ({ ...s, cpf: e.target.value }))} className="rounded-none mt-1 font-mono" placeholder="000.000.000-00" />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose} className="rounded-none">Cancelar</Button>
              <Button data-testid="pix-generate-btn" onClick={generate} disabled={submitting} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                {submitting ? "Gerando…" : "Gerar Pix"}
              </Button>
            </DialogFooter>
          </div>
        ) : checkout.status === "approved" ? (
          <div className="text-center py-6" data-testid="pix-approved">
            <CheckCircle size={56} weight="fill" className="text-[#0F4C3A] mx-auto" />
            <h3 className="text-xl font-black mt-3">Pagamento aprovado!</h3>
            <p className="text-sm text-[#5C5C59] mt-2">Empresa reativada. Próxima cobrança em 30 dias.</p>
            <Button onClick={onClose} className="mt-5 rounded-none bg-[#0F4C3A] hover:bg-[#0A3629] text-white">Fechar</Button>
          </div>
        ) : (
          <div>
            <p className="text-2xl font-black">R$ {checkout.amount_brl.toFixed(2).replace(".", ",")} — {checkout.plan_name}</p>
            <div className="grid md:grid-cols-2 gap-4 mt-3">
              <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-[#5C5C59]">QR Code</p>
                {checkout.qr_code_base64 && (
                  <img src={`data:image/png;base64,${checkout.qr_code_base64}`} alt="Pix QR" className="w-full max-w-[240px] border mt-1" />
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-[#5C5C59]">Copia e cola</p>
                <div className="mt-1 p-2 bg-[#F7F7F5] border border-[#E5E5E0] font-mono text-[10px] break-all max-h-24 overflow-y-auto">
                  {checkout.qr_code}
                </div>
                <Button onClick={copyCode} className="rounded-none w-full mt-2 bg-[#0F4C3A] hover:bg-[#0A3629] text-white text-xs" data-testid="pix-copy-btn">
                  <Copy size={12} weight="bold" className="mr-1" /> {copied ? "Copiado!" : "Copiar código"}
                </Button>
                <p className="text-[10px] text-[#D97706] mt-2">Atualiza automaticamente a cada 5s.</p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
